import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ProductDialog({ open, onOpenChange, categories, editing, onDone }) {
    const [name, setName] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [quantity, setQuantity] = useState("0");
    const [purchasePrice, setPurchasePrice] = useState("0");
    const [salePrice, setSalePrice] = useState("0");
    const [threshold, setThreshold] = useState("5");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setName(editing.name);
            setCategoryId(editing.category_id);
            setQuantity(String(editing.quantity));
            setPurchasePrice(String(editing.purchase_price ?? 0));
            setSalePrice(String(editing.sale_price ?? 0));
            setThreshold(String(editing.low_stock_threshold));
        } else {
            setName("");
            setCategoryId(categories[0]?.id || "");
            setQuantity("0");
            setPurchasePrice("0");
            setSalePrice("0");
            setThreshold("5");
        }
    }, [open, editing, categories]);

    const submit = async () => {
        if (!name.trim()) return toast.error("Nom requis");
        if (!categoryId) return toast.error("Catégorie requise");
        setLoading(true);
        try {
            if (editing) {
                await api.patch(`/products/${editing.id}`, {
                    name,
                    category_id: categoryId,
                    purchase_price: parseFloat(purchasePrice) || 0,
                    sale_price: parseFloat(salePrice) || 0,
                    low_stock_threshold: parseInt(threshold, 10) || 0,
                });
                toast.success("Produit mis à jour");
            } else {
                await api.post("/products", {
                    name,
                    category_id: categoryId,
                    quantity: parseInt(quantity, 10) || 0,
                    purchase_price: parseFloat(purchasePrice) || 0,
                    sale_price: parseFloat(salePrice) || 0,
                    low_stock_threshold: parseInt(threshold, 10) || 0,
                });
                toast.success("Produit créé");
            }
            onOpenChange(false);
            onDone && onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="product-dialog" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                        {editing ? "Modifier le produit" : "Nouveau produit"}
                    </DialogTitle>
                    <DialogDescription>
                        {editing
                            ? "Modifiez les informations du produit."
                            : "Ajoutez un nouveau produit à votre inventaire."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Nom du produit
                        </Label>
                        <Input
                            data-testid="product-name-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Ex : AK-47, Cocaïne, Métal…"
                            className="mt-2 h-11"
                        />
                    </div>

                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                            Catégorie
                        </Label>
                        <Select value={categoryId} onValueChange={setCategoryId}>
                            <SelectTrigger
                                data-testid="product-category-select"
                                className="mt-2 h-11"
                            >
                                <SelectValue placeholder="Choisir…" />
                            </SelectTrigger>
                            <SelectContent>
                                {categories.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>
                                        {c.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Prix d&apos;achat
                            </Label>
                            <Input
                                data-testid="product-purchase-price-input"
                                type="number"
                                min="0"
                                step="0.01"
                                value={purchasePrice}
                                onChange={(e) => setPurchasePrice(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Prix de vente
                            </Label>
                            <Input
                                data-testid="product-sale-price-input"
                                type="number"
                                min="0"
                                step="0.01"
                                value={salePrice}
                                onChange={(e) => setSalePrice(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        {!editing && (
                            <div>
                                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    Quantité init.
                                </Label>
                                <Input
                                    data-testid="product-quantity-input"
                                    type="number"
                                    min="0"
                                    value={quantity}
                                    onChange={(e) => setQuantity(e.target.value)}
                                    className="mt-2 h-11"
                                />
                            </div>
                        )}
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
                                Seuil bas
                            </Label>
                            <Input
                                data-testid="product-threshold-input"
                                type="number"
                                min="0"
                                value={threshold}
                                onChange={(e) => setThreshold(e.target.value)}
                                className="mt-2 h-11"
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        data-testid="product-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={loading}
                        data-testid="product-submit-btn"
                        className="bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                    >
                        {loading ? "Enregistrement…" : editing ? "Enregistrer" : "Créer"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
